/**
 * JS-side primitive source — UI-v2 Phase 0, Slice 0.
 *
 * Single source of truth for palettes that are consumed as literal hex strings
 * in JS/TS code (ECharts series, avatar hash palette, fit-score tier colors,
 * Analysis scorecard verdict colors). These are the ONLY hex literals
 * permitted outside of primitives.css / resume-doc.css / fonts.css.
 *
 * Values must be byte-identical to the corresponding --p-* vars in
 * src/styles/primitives.css. A rebrand updates primitives.css AND this file
 * in the same commit.
 *
 * Slices 1+ will rewire echartsTheme.ts / initials.ts / fitScore.ts /
 * parsers.ts to import from this file instead of duplicating literals.
 * Slice 0 creates the exports; consumers are rewired in Slice 1.
 *
 * See: backend/docs/cofounder_brief_ui_v2_theming_architecture.md §2.
 */

// ── Avatar hash palette ────────────────────────────────────────────────────
// Mirror of --p-avatar-{0..9} in primitives.css.
// Matches initials.ts AVATAR_COLORS exactly (same order, same hex).
export const AVATAR_COLORS: readonly string[] = [
  "#6366F1", // --p-avatar-0  indigo
  "#0EA5E9", // --p-avatar-1  sky
  "#10B981", // --p-avatar-2  emerald
  "#F59E0B", // --p-avatar-3  amber
  "#EF4444", // --p-avatar-4  red
  "#8B5CF6", // --p-avatar-5  violet
  "#EC4899", // --p-avatar-6  pink
  "#14B8A6", // --p-avatar-7  teal
  "#F97316", // --p-avatar-8  orange
  "#06B6D4", // --p-avatar-9  cyan
] as const;

// ── ECharts series palette ─────────────────────────────────────────────────
// Mirror of echartsTheme.ts color array — kept in sync with --p-* vars.
// Slice 1 rewires echartsTheme.ts to import ECHARTS_COLORS from here.
export const ECHARTS_COLORS: readonly string[] = [
  "#0077FF", // --p-blue-500   brand primary
  "#00E5CC", // --p-teal-500   brand teal
  "#00C896", // --p-green-500  positive
  "#F59E0B", // --p-amber-500  warn
  "#EF4444", // --p-red-500    negative
] as const;

// ECharts axis / grid colors
export const ECHARTS_AXIS_COLOR = "#D4DAE4";   // chart axis line (= --p-slate-150)
export const ECHARTS_SPLIT_COLOR = "#EEF2FF";  // chart split line (= --p-offwhite)

// ECharts admin dashboard chart colors (Slice 6 — SA console panels)
export const ECHARTS_AXIS_LABEL_COLOR = "#7A8AA5";         // axis label text (= --p-slate-600)
export const ECHARTS_TOOLTIP_BG = "#192338";               // tooltip background (= --p-navy-800)
export const ECHARTS_TOOLTIP_BORDER = "rgba(255,255,255,0.08)"; // tooltip border / axisLine
export const ECHARTS_TEAL_DIM = "#00C8B8";                 // bar/line dim accent (= --p-teal-600)
export const ECHARTS_TEAL_AREA_START = "rgba(0,229,204,0.18)"; // area gradient start (--p-teal-500 @18%)
export const ECHARTS_TEAL_AREA_END = "rgba(0,229,204,0)";     // area gradient end  (--p-teal-500 @0%)
export const ECHARTS_HEATMAP_START = "rgba(0,229,204,0.1)";   // heatmap low-end    (--p-teal-500 @10%)

// ── White-label branding ────────────────────────────────────────────────────
// The CertAIn default accent shown in the Branding settings color-picker before
// a tenant picks its own. Mirrors the candidate write-up PDF's default accent
// (= --p-blue-500 brand primary). A tenant's chosen hex lives in component state,
// never as a source literal — this is the only branding-accent literal.
export const BRAND_ACCENT_DEFAULT = "#0077FF";

// ── QR code fg/bg ──────────────────────────────────────────────────────────
// TOTP scanners require high-contrast pure black/white — never theme-switch.
// Used by QRCode.tsx as bgColor / fgColor props on QRCodeSVG.
export const QR_COLOR_BG = "#FFFFFF"; // --p-white
export const QR_COLOR_FG = "#000000"; // --p-black (pure black, no --p- primitive; QR-specific)

// ── Fit-score tier hex ─────────────────────────────────────────────────────
// Mirrors fitScore.ts fitColorHex() return values.
// Slice 1 rewires fitScore.ts to import from here.
export const FIT_COLOR_POSITIVE = "#00C896"; // --p-green-500  >= 78
export const FIT_COLOR_BLUE     = "#3B82F6"; // --p-blue-500a  >= 62
export const FIT_COLOR_WARN     = "#F59E0B"; // --p-amber-500  < 62

// ── Verdict / scorecard colors ─────────────────────────────────────────────
// Mirrors parsers.ts VERDICT_COLORS table.
// Slice 1 rewires parsers.ts to import from here.
export const VERDICT_COLORS = {
  POSITIVE: "#00C896", // --p-green-500
  STRONG:   "#00C896", // --p-green-500
  GAP:      "#EF4444", // --p-red-500
  WEAK:     "#F59E0B", // --p-amber-500
  WATCH:    "#F59E0B", // --p-amber-500
  MIXED:    "#3B82F6", // --p-blue-500a
} as const;

// ── Section colors (Analysis tab) ─────────────────────────────────────────
// Mirrors parsers.ts SECTION_COLORS table (post-interview Analysis tab only).
// Slice 1 rewires parsers.ts to import from here.
export const SECTION_COLORS = {
  strengths:  "#10B981", // --p-emerald-500
  gaps:       "#EF4444", // --p-red-500
  probe:      "#EF4444", // --p-red-500
  technical:  "#8B5CF6", // --p-violet-500
  tenure:     "#F59E0B", // --p-amber-500
  culture:    "#6366F1", // --p-indigo-500
  leadership: "#6366F1", // --p-indigo-500
} as const;

// ── Verdict null-fallback (slate) ──────────────────────────────────────────
// Used by parsers.ts verdictColor() when verdict is null.
// No --p-* alias yet (chart-specific slate); distinct from avatar palette entries.
export const VERDICT_NULL_COLOR = "#94A3B8"; // slate-400

// ── Terminal / opted-out base color ────────────────────────────────────────
// Used for "OUT" badge bg, terminal card border-top, PipelineStatusPanel opted_out.
// Matches --p-slate-650 in primitives.css.
export const COLOR_TERMINAL = "#475569"; // --p-slate-650

// ── Pipeline status colors (JS-consumed in PipelineStatusPanel) ────────────
// Each resolves to the corresponding --p-* primitive.
export const PIPELINE_STATUS_COLORS = {
  active:               "#3B82F6", // --p-blue-500a  (= FIT_COLOR_BLUE)
  phone_screen:         "#F59E0B", // --p-amber-500  (= FIT_COLOR_WARN)
  interview_scheduled:  "#F97316", // --p-orange-500
  interviewed:          "#00E5CC", // --p-teal-500
  offer:                "#10B981", // --p-emerald-500
  hired:                "#00C896", // --p-green-500  (= FIT_COLOR_POSITIVE)
  rejected:             "#EF4444", // --p-red-500    (= VERDICT_COLORS.GAP)
  opted_out:            "#475569", // --p-slate-650
} as const;

// ── Rank badge decorative palette (CanonCandidateCard podium ranks 1/2/3) ──
// These are cosmetic-only podium badge colors not part of the semantic token
// system. Stored here (the only permitted JS hex home) rather than inlined.
export const RANK_BADGE_GOLD_START   = "#FFD700"; // podium 1 gradient start (gold)
export const RANK_BADGE_SILVER_START = "#D1D5DB"; // podium 2 gradient start (silver)
export const RANK_BADGE_SILVER_END   = "#9CA3AF"; // podium 2 gradient end   (silver dark)
export const RANK_BADGE_BRONZE_START = "#CD7F32"; // podium 3 gradient start (bronze)
export const RANK_BADGE_BRONZE_END   = "#B45309"; // podium 3 gradient end   (bronze dark)

// Podium rank badge box-shadows (rgba strings — too alpha-specific for CSS vars).
export const RANK_BADGE_GOLD_SHADOW   = "0 2px 8px rgba(255,165,0,0.35)";
export const RANK_BADGE_SILVER_SHADOW = "0 2px 8px rgba(156,163,175,0.30)";
export const RANK_BADGE_BRONZE_SHADOW = "0 2px 8px rgba(180,83,9,0.30)";

// ── Tenure chip colors (CanonCandidateCard TenureChip inline styles) ───────
// These differ from the verdict-* tokens (different alphas; tenure-specific).
export const TENURE_STRONG_BG   = "rgba(0,200,150,0.08)";   // --p-green-500 at 8%
export const TENURE_STRONG_FG   = "#6EE7B7";                 // --p-green-300
export const TENURE_STRONG_RING = "rgba(0,200,150,0.15)";   // --p-green-500 at 15%

export const TENURE_MODERATE_BG   = "rgba(245,158,11,0.08)"; // --p-amber-500 at 8%
export const TENURE_MODERATE_FG   = "#FCD34D";                // --p-amber-300
export const TENURE_MODERATE_RING = "rgba(245,158,11,0.15)"; // --p-amber-500 at 15%

export const TENURE_WEAK_BG   = "rgba(239,68,68,0.08)";     // --p-red-500 at 8%
export const TENURE_WEAK_FG   = "#FCA5A5";                   // --p-red-200 (Tailwind red-300)
export const TENURE_WEAK_RING = "rgba(239,68,68,0.15)";     // --p-red-500 at 15%

// ── LinkedIn brand color ───────────────────────────────────────────────────
// Permanent third-party brand color — not a CertAIn primitive.
// Used by CandidatePageSidebar LinkedIn pill button.
export const LINKEDIN_BLUE = "#0A66C2"; // LinkedIn brand blue

// ── Accent teal (--p-teal-500) ────────────────────────────────────────────
// Fallback tint for VerdictCard when no verdict is extracted (prototype L689).
// Byte-identical to --p-teal-500 / --accent-secondary (dark).
export const ACCENT_TEAL = "#00E5CC"; // --p-teal-500

// ── Subtle overlay constants (prototype nav hover/active) ─────────────────
// Fixed white-overlay constants from the prototype nav CSS.
// Used as inline style values in ScorecardGrid and DetailedAssessmentSplit.
// These are design-level primitives (no semantic token alias at this opacity level).
export const NAV_HOVER_BG_02 = "rgba(255,255,255,.02)";  // sc-row hover bg
export const NAV_HOVER_BG_04 = "rgba(255,255,255,.04)";  // prep-nav-item hover bg
export const NAV_ACTIVE_BG_05 = "rgba(255,255,255,.05)"; // prep-nav-item active bg

// ── Slice 7 additions ──────────────────────────────────────────────────────

// Teal ramp additions
export const COLOR_TEAL_600 = "#00C8B8"; // --p-teal-600 (brand-grad end dark)
export const COLOR_TEAL_800 = "#0E8A7C"; // --p-teal-800 (section-recommendation light)
export const COLOR_TEAL_300 = "#5EEDD9"; // --p-teal-300 (soft teal; mock series)

// Navy/ink additions
export const COLOR_NAVY_800 = "#192338"; // --p-navy-800 (surface-subtle dark)
export const COLOR_NAVY_850 = "#131B2E"; // --p-navy-850 (surface-elevated dark)
export const COLOR_NAVY_900 = "#0D1220"; // --p-navy-900 (surface-canvas dark)
export const COLOR_INK_600  = "#3A4A6A"; // --p-ink-600 (mid-navy mock chart)
export const COLOR_INK_800  = "#2D3748"; // --p-ink-800 (dark slate; mock light-mode text)
export const COLOR_INK_900  = "#0D1525"; // --p-ink-900 (text-primary light / tooltip text)

// Slate additions
export const COLOR_SLATE_400 = "#5B6680"; // --p-slate-400 (text-secondary light)
export const COLOR_SLATE_500 = "#5A6B85"; // --p-slate-500 (text-disabled dark)
export const COLOR_SLATE_600 = "#7A8AA5"; // --p-slate-600 (text-tertiary dark / chart axis)
export const COLOR_SLATE_700 = "#9FB3CC"; // --p-slate-700 (text-secondary dark / chart axis)

// Green additions
export const COLOR_GREEN_400 = "#7BCBA8"; // --p-green-400 (soft green; mock series)
// COLOR_GREEN_700 (#00754D) and COLOR_GREEN_SOFT (#7CE0BB) referenced via Tailwind utilities text-green-700 / text-green-350

// Blue additions
export const COLOR_BLUE_350  = "#7DA8E8"; // --p-blue-350 (soft blue; mock series)
export const COLOR_VIOLET_500 = "#8B5CF6"; // --p-violet-500 (section-technical)
export const COLOR_VIOLET_300 = "#C5B3F0"; // --p-violet-300 (soft violet; mock series)

// Amber/red additions
export const COLOR_AMBER_250 = "#F4C77A"; // --p-amber-250 (soft amber; mock series)
export const COLOR_RED_250   = "#F4A0A0"; // --p-red-250 (soft red; mock series)
export const COLOR_OFFWHITE  = "#EEF2FF"; // --p-offwhite (text/bg off-white)
export const COLOR_WHITE     = "#FFFFFF";  // --p-white (pure white)

// ── RGB component strings for rgba() template literals ─────────────────────
// Format: "R,G,B" — use as: `rgba(${RGB_FOO},alpha)`
export const RGB_WHITE     = "255,255,255"; // pure white
export const RGB_TEAL_500  = "0,229,204";   // --p-teal-500
export const RGB_TEAL_600  = "0,200,184";   // --p-teal-600 = #00c8b8
export const RGB_TEAL_300  = "94,237,217";  // --p-teal-300 = #5eedd9
export const RGB_BLUE_500  = "0,119,255";   // --p-blue-500
export const RGB_GREEN_500 = "0,200,150";   // --p-green-500 = #00c896
export const RGB_NAVY_900   = "13,18,32";    // --p-navy-900 = #0d1220
// RGB_NAVY_800 / RGB_INK_900 not needed — hero glows use CSS classes
export const RGB_VIOLET_500 = "139,92,246";  // --p-violet-500 = #8b5cf6

// Hero glow glows use CSS classes hero-glow-blue / hero-glow-teal (index.css)
// COLOR_BRAND_BLUE_RGB / COLOR_BRAND_TEAL_RGB not needed as JS imports

